// View-2 — Continuous Timeline (design spec §7; frontend plan §4.7).
//
// day-list → UTC+0 day picker → day-ranges → horizontal axis (reusing
// features/playback/timeline.ts math + TimelineStrip) → scrub → request that
// segment's playlist. Covers c1–c7:
//   c1 day-list (UTC+0)            c2 day-ranges axis
//   c3 zoom 5min..1day + scrub→playlist + playhead + app-side delay (default 2s)
//   c4 stop previous + release on move (HlsPlayer destroys on source change)
//   c5 recycle-aware refresh (re-query day-ranges on /seg 404)
//   c6 max 5-min play window       c7 event markers overlaid
//
// Gated behind pb.continuousTimeline by the integrator's route. No App.tsx edits here.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TimelineStrip } from '../../features/playback/TimelineStrip';
import { coalesceSegments, type ClipSpan, type Segment, type TimeRange } from '../../features/playback/timeline';
import { pbPlaylistUrl } from '../../lib/recPlayback/hlsSource';
import { openRemotePlayback, type RemotePlaybackSession } from '../../lib/recPlayback/webrtcPlayback';
import { makeDataChannelLoaderClass } from '../../lib/recPlayback/dataChannelLoader';
import { recFlag } from '../../lib/recPlayback/flags';
import { queryRangeEvents } from '../../lib/recPlayback/sdcardQuery';
import { clusterOf } from '../../features/events/eventsLib';
import { useDayList, useDayRanges, useLiveEvents, useReconciledTimeline } from './useRecPlayback';
import { HlsPlayer, type HlsSource, type HlsLoaderFactory } from './HlsPlayer';
import './recPlayback.css';

const DAY_US = 86_400_000_000;
const DAY_MS = 86_400_000;
/** REC_CONT_MIN_WINDOW_US — finest zoom / app-side render floor (device has no floor, §7.3). */
const MIN_WINDOW_US = 60_000_000; // 1 min
/** REC_PLAYBACK_MAX_US — single play span ≤ 5 min (c6). */
const MAX_PLAY_US = 300_000_000;
const DEFAULT_SCRUB_DELAY_MS = 1000;
/** Live-tail: poll cadence for newly-flushed recording (recordda accumulates ~15s windows). */
const LIVE_TAIL_POLL_MS = 5000;
/** Live-tail: stop tailing after this long with no new recording (recording has stopped). */
const LIVE_TAIL_GIVEUP_MS = 60_000;
/** Prefetch lead: start refetching day-ranges this long before the live chunk ends, so the
 *  next flush is already known when it ends → seamless continue instead of a stall. */
const LIVE_PREFETCH_LEAD_US = 4_000_000;
/** Min spacing between proactive prefetch refetches while nearing the live frontier. */
const LIVE_PREFETCH_THROTTLE_MS = 2000;
/** Activating an event marker starts playback this far before the event (lead-up). */
const EVENT_LEAD_US = 5_000_000;

const ZOOM_OPTIONS_US = [MIN_WINDOW_US, 5 * 60_000_000, 15 * 60_000_000, 60 * 60_000_000, 6 * 60 * 60_000_000, DAY_US];
/** day_index → [day_start, day_end) µs, where a "day" is midnight-to-midnight in the
 *  display timezone (offsetUs = UTC offset in µs; 0 = UTC+0). */
function dayWindow(dayIndex: number, offsetUs: number): { fromUs: number; toUs: number } {
  const fromUs = dayIndex * DAY_US - offsetUs;
  return { fromUs, toUs: fromUs + DAY_US };
}

/** Which display-timezone day a µs epoch falls in. */
function dayIndexOf(us: number, offsetUs: number): number {
  return Math.floor((us + offsetUs) / DAY_US);
}

/** UTC offset minutes → "UTC+8" / "UTC-3" / "UTC+0". */
function fmtTzOffset(min: number): string {
  const h = min / 60;
  return `UTC${h >= 0 ? '+' : '−'}${Math.abs(h)}`;
}

export interface ContinuousTimelineViewProps {
  device: { id: string; name: string };
  /** Same-LAN device base URL for VOD playback (frontend plan Open Q-1). */
  deviceHlsBase?: string;
  /** App-side scrub→play delay (ms), default 2000 (c3). */
  scrubDelayMs?: number;
}

export function ContinuousTimelineView({ device, deviceHlsBase, scrubDelayMs = DEFAULT_SCRUB_DELAY_MS }: ContinuousTimelineViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deviceId = device.id;

  const dayList = useDayList(deviceId);
  // Display timezone is the browser's LOCAL zone — shown to the user, not selectable.
  // It drives all day boundaries + time labels.
  const offsetMin = -new Date().getTimezoneOffset();
  const offsetUs = offsetMin * 60_000_000;
  const [dayIndex, setDayIndex] = useState<number>(() => dayIndexOf(Date.now() * 1000, offsetUs));
  const [zoomUs, setZoomUs] = useState<number>(DAY_US);
  const [viewStartUs, setViewStartUs] = useState<number>(() => dayWindow(dayIndexOf(Date.now() * 1000, offsetUs), offsetUs).fromUs);
  const [playheadUs, setPlayheadUs] = useState<number | null>(null);
  // Live-tail: the µs we've played up to at the live edge and are polling past for more
  // recording (null = not tailing). Declared up here as the day-reset effect clears it.
  const [liveTailUs, setLiveTailUs] = useState<number | null>(null);
  const [source, setSource] = useState<HlsSource | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // REMOTE playback (spec D8 / §8A.3): the cloud webapp can't reach the device LAN
  // http endpoint, so when no deviceHlsBase is given we tunnel /pb.m3u8 + /seg over a
  // WebRTC data channel. The session (one RTCPeerConnection + data channel) is opened
  // lazily on first play and reused across scrubs; the hls.js loader routes every
  // fragment through its PbTunnel. Gated behind REC_FEAT_PLAYBACK_REMOTE.
  const remoteEnabled = recFlag('REC_FEAT_PLAYBACK_REMOTE');
  const sessionRef = useRef<RemotePlaybackSession | null>(null);
  const [loaderFactory, setLoaderFactory] = useState<HlsLoaderFactory | undefined>(undefined);

  const day = useMemo(() => dayWindow(dayIndex, offsetUs), [dayIndex, offsetUs]);
  // continuous timeline: only genuine continuous-recording coverage (device filters out
  // event clips / snapshots), per the "只顯示 continuous+sdcard" requirement.
  const dayRanges = useDayRanges(deviceId, day.fromUs, day.toUs, true);

  // event markers for the selected day (c7) — reconciled read INCLUDES cloud events, which
  // outlive the sdcard clip ring, so events on a recycled/older day still show.
  const markers = useReconciledTimeline([device], { fromUs: day.fromUs, toUs: day.toUs });
  // record_mode straight from the sdcard range-events, keyed by EVENT TIMESTAMP. The cloud
  // aggregate drops record_mode, and its eid differs from the sdcard eid — so eid-matching
  // failed for read-back events; the event's minted ts is identical on both sides, so we
  // match on ts to attach the mode. Empty for recycled days (sdcard gone) → no label there.
  const [modeByTs, setModeByTs] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    let alive = true;
    queryRangeEvents(deviceId, day.fromUs, day.toUs)
      .then((res) => {
        if (!alive) return;
        const m = new Map<number, string>();
        for (const ev of res.items ?? []) if (ev.record_mode) m.set(ev.event_ts_us, ev.record_mode);
        setModeByTs(m);
      })
      .catch(() => { /* leave empty — markers render without a mode */ });
    return () => { alive = false; };
  }, [deviceId, day.fromUs, day.toUs]);

  const scrubTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current);
      sessionRef.current?.stop();
      sessionRef.current = null;
    };
  }, []);

  // When the day changes, reset the viewport to the day window and clear the player.
  // Re-arm auto-positioning too: re-entering the latest-record day re-homes the playhead
  // to the newest record, while switching to any other day correctly leaves it cleared.
  const positionedDayRef = useRef<number | null>(null);
  useEffect(() => {
    setViewStartUs(day.fromUs);
    setZoomUs(DAY_US);
    setSource(null);
    setPlayheadUs(null);
    setLiveTailUs(null); // leaving the day stops any live-tailing
    positionedDayRef.current = null; // re-arm positioning for the day we just entered
  }, [day.fromUs]);

  // The newest display-timezone day that has recordings (sdcard day-list is keyed by UTC
  // day, so map each to the tz day its tail falls in). The playhead auto-homes to the
  // latest record only on THIS day; every other day stays bare.
  const latestRecordDay = useMemo(() => {
    if (!dayList.days.length) return null;
    let m = -Infinity;
    // Clamp the UTC day's end to "now": today's still-incomplete UTC day must not map to a
    // FUTURE tz day (UTC 7/2 tail → UTC+8 7/3) before that local day has actually begun.
    for (const d of dayList.days) m = Math.max(m, dayIndexOf(Math.min(d.day_index * DAY_US + DAY_US - 1, Date.now() * 1000), offsetUs));
    return Number.isFinite(m) ? m : null;
  }, [dayList.days, offsetUs]);

  // First load only: jump the day picker to the latest-record day.
  const didInitDayRef = useRef(false);
  useEffect(() => {
    if (didInitDayRef.current || latestRecordDay === null) return;
    didInitDayRef.current = true;
    setDayIndex(latestRecordDay);
  }, [latestRecordDay]);

  // Switching display timezone re-bases the day index (it now means a tz-day): land back on
  // the latest-record day in the new zone, else today there. Guarded so it only fires on an
  // actual tz change, not when latestRecordDay updates for other reasons.
  const prevOffsetRef = useRef(offsetMin);
  useEffect(() => {
    if (prevOffsetRef.current === offsetMin) return;
    prevOffsetRef.current = offsetMin;
    setDayIndex(latestRecordDay ?? dayIndexOf(Date.now() * 1000, offsetUs));
  }, [offsetMin, offsetUs, latestRecordDay]);

  // On entering the latest-record day (initial load OR switching back to it), home the
  // playhead onto the newest record. The zoom stays at the full-day window set by the
  // day-reset effect above (req: re-entering always opens at 1-day zoom); the user zooms
  // in from there. Runs once per day-entry (positionedDayRef) so scrubbing and recycle-
  // reloads don't yank it back; the in-day filter ignores a stale fetch from the day we
  // just left so it can't mis-place the playhead.
  useEffect(() => {
    if (positionedDayRef.current === dayIndex) return; // already homed for this entry
    if (dayIndex !== latestRecordDay) return; // other days: leave the playhead cleared
    const inDay = dayRanges.ranges.filter((r) => r.end_us > day.fromUs && r.start_us < day.toUs);
    if (inDay.length === 0) return; // wait for THIS day's ranges (ignore stale/empty)
    positionedDayRef.current = dayIndex;
    const latestEndUs = Math.min(day.toUs, inDay.reduce((m, r) => Math.max(m, r.end_us), day.fromUs));
    setPlayheadUs(latestEndUs); // position only — playback waits for the user (只定位不自動播)
  }, [dayRanges.ranges, dayIndex, latestRecordDay, day.fromUs, day.toUs]);

  // Viewport: zoomUs wide, clamped within the day (single-day view, midnight clip §7.3.2).
  const viewport: TimeRange = useMemo(() => {
    const span = Math.max(MIN_WINDOW_US, Math.min(zoomUs, DAY_US));
    const start = Math.max(day.fromUs, Math.min(viewStartUs, day.toUs - span));
    return { fromUs: start, toUs: start + span };
  }, [zoomUs, viewStartUs, day.fromUs, day.toUs]);

  // Zoom change recenters the new span on the current anchor (the playhead if set,
  // else the current viewport center) and grows outward — not anchored to the left
  // edge — then clamps within the day. So 5m/15m/60m stay centered on where you are.
  const onZoomChange = useCallback(
    (nextZoom: number) => {
      const nextSpan = Math.max(MIN_WINDOW_US, Math.min(nextZoom, DAY_US));
      const curSpan = Math.max(MIN_WINDOW_US, Math.min(zoomUs, DAY_US));
      const curStart = Math.max(day.fromUs, Math.min(viewStartUs, day.toUs - curSpan));
      const anchor = playheadUs ?? curStart + curSpan / 2;
      const nextStart = Math.max(day.fromUs, Math.min(anchor - nextSpan / 2, day.toUs - nextSpan));
      setZoomUs(nextZoom);
      setViewStartUs(nextStart);
    },
    [zoomUs, viewStartUs, playheadUs, day.fromUs, day.toUs]
  );

  // day-ranges → ClipSpan[] for the strip; coalescing math lives in timeline.ts.
  const clips: ClipSpan[] = useMemo(
    () => dayRanges.ranges.map((r, i) => ({ clip_id: `r${i}`, clip_start_ts: r.start_us, clip_end_ts: r.end_us })),
    [dayRanges.ranges]
  );

  // Contiguous recording blocks over the whole day (clips <2s apart merged). Used to make
  // playback coverage-aware: a play request must START inside a block and must END at that
  // block's edge — so it never bleeds across a gap into the next recording, and clicking a
  // bare (no-recording) spot issues no request at all.
  const daySegments = useMemo<Segment[]>(
    () => coalesceSegments(clips, { fromUs: day.fromUs, toUs: day.toUs }),
    [clips, day.fromUs, day.toUs]
  );
  const segmentAt = useCallback(
    (us: number): Segment | null => daySegments.find((s) => us >= s.startUs && us < s.endUs) ?? null,
    [daySegments]
  );

  // Base events come from the reconciled read (includes cloud so recycled-day events show);
  // the live tail appends the newest. Merge by eid.
  const baseMaxTs = useMemo(
    () => markers.rows.reduce((m, { row }) => Math.max(m, row.event_ts_us), day.fromUs),
    [markers.rows, day.fromUs]
  );
  const liveRows = useLiveEvents(deviceId, day.toUs, dayIndex === latestRecordDay, baseMaxTs);

  const timelineEvents = useMemo(() => {
    const byEid = new Map<string, { tsUs: number; typeKey: string; mode?: string }>();
    // Show all events; each event's record_mode comes from the sdcard modeByTs map (matched on
    // timestamp), or from the row/live value when present — so the tooltip labels continuous vs
    // event-mode. Events on recycled days still show (from cloud) but without a mode label.
    const put = (eid: string, tsUs: number, typeKey: string, mode?: string) => {
      const cur = byEid.get(eid);
      const m = mode ?? modeByTs.get(tsUs);
      if (!cur) byEid.set(eid, { tsUs, typeKey, mode: m });
      else if (!cur.mode && m) cur.mode = m;
    };
    for (const { row } of markers.rows) {
      put(row.eid, row.event_ts_us, row.event_id_hex ?? row.event_type ?? '', row.record_mode);
    }
    for (const e of liveRows) {
      put(e.eid, e.tsUs, e.eventIdHex ?? e.eventType ?? '', e.recordMode);
    }
    return [...byEid.values()].map((e) => {
      const cls = clusterOf(e.typeKey); // type emoji + localized label
      return { event_ts: e.tsUs, icon: cls.icon, typeLabel: cls.label, mode: e.mode };
    });
  }, [markers.rows, liveRows, modeByTs]);

  // Open (or reuse) the remote data-channel session and return an hls.js loader bound
  // to its PbTunnel. The session is cached in sessionRef and reused across scrubs.
  const ensureRemoteLoader = useCallback(async (): Promise<HlsLoaderFactory> => {
    if (!sessionRef.current) {
      sessionRef.current = await openRemotePlayback(deviceId);
    }
    const tunnel = sessionRef.current.tunnel;
    return () => makeDataChannelLoaderClass({ tunnel, onRecycle404: dayRanges.reload });
  }, [deviceId, dayRanges]);

  // playhead-follows-playback + continuous-advance state:
  //  - playStartUsRef / playEndUsRef: the [start,end) epoch µs of the clip currently
  //    loaded — currentTime maps back to an absolute position, and we know where the NEXT
  //    chunk begins. playStartUsRef null = follow suspended (no clip / awaiting load).
  //  - scrubbingRef: true while dragging — suppresses follow so the drag wins.
  //  - liveTailUs: set when we've played to the end of the latest recording and are polling
  //    for more (live-view-style tail) from this µs; null = not tailing.
  const playStartUsRef = useRef<number | null>(null);
  const playEndUsRef = useRef<number | null>(null);
  const scrubbingRef = useRef(false);
  const lastPlayedUsRef = useRef<number | null>(null);
  const lastPrefetchAtRef = useRef(0);
  // dayRanges.reload is a fresh closure each render; hold it in a ref so prefetch + the
  // live-tail poll can fire it without re-subscribing (and resetting the give-up timer).
  const reloadRef = useRef(dayRanges.reload);
  reloadRef.current = dayRanges.reload;

  // Load + play [us, min(us+window, segEnd, dayEnd)] IMMEDIATELY (no debounce). Span capped
  // at 5 min (c6) and never across a gap. Stamps playStart/EndUsRef so the playhead can
  // follow and the next chunk can chain on. Used for programmatic advance/tail.
  const startPlayback = useCallback(
    async (us: number, segEndUs: number) => {
      const endUs = Math.min(us + Math.min(zoomUs, MAX_PLAY_US), segEndUs, day.toUs);
      // Playlist range is HALF-OPEN [us, endUs): the segment whose ts == endUs belongs to the
      // NEXT chunk (onPlayEnded chains at endUs). The device serves ts in [t1,t2] INCLUSIVE,
      // so we request t2 = endUs-1µs; otherwise the boundary segment is served by BOTH this
      // chunk and the next → it replays ~1s at every chunk boundary. (No effect mid-segment:
      // there is no segment exactly at endUs to drop.) playEndUsRef keeps endUs for chaining.
      const playlistEndUs = endUs - 1;
      if (deviceHlsBase) {
        setLoaderFactory(undefined);
        playStartUsRef.current = us;
        playEndUsRef.current = endUs;
        setSource({ kind: 'device-vod', m3u8Url: pbPlaylistUrl(deviceHlsBase, us, playlistEndUs) });
        return;
      }
      if (!remoteEnabled) {
        setNotice(t('recPlayback.sdcardLanOnly'));
        return;
      }
      // REMOTE: open/reuse the WebRTC session, then play a tunnel-relative URL whose
      // fragments hls.js fetches through the data-channel loader.
      try {
        const lf = await ensureRemoteLoader();
        if (!aliveRef.current) return;
        setLoaderFactory(() => lf);
        playStartUsRef.current = us;
        playEndUsRef.current = endUs;
        setSource({ kind: 'device-vod', m3u8Url: pbPlaylistUrl('', us, playlistEndUs) });
      } catch {
        if (aliveRef.current) setNotice(t('recPlayback.deviceUnreachable'));
      }
    },
    [deviceHlsBase, remoteEnabled, ensureRemoteLoader, zoomUs, day.toUs, t]
  );

  // User-initiated play: debounced by the app-side delay (c3) so a fast scrub doesn't fire
  // a play per pixel; the current player stays mounted until the delayed request resolves.
  const requestPlay = useCallback(
    (us: number, segEndUs: number) => {
      if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current);
      scrubTimerRef.current = setTimeout(() => {
        if (aliveRef.current) void startPlayback(us, segEndUs);
      }, scrubDelayMs);
    },
    [startPlayback, scrubDelayMs]
  );

  // Preview while dragging: move the red line only, do NOT (re)start playback yet (c4 —
  // playback waits for release so a fast scrub doesn't fire a play per pixel). Mark the
  // scrub so the playing clip's progress doesn't fight the user's drag for the playhead.
  const onSeek = useCallback((us: number) => {
    scrubbingRef.current = true;
    setPlayheadUs(us);
    setNotice(null);
  }, []);

  // Commit on release: (re)start playback at the new spot. Skip when it's the same spot
  // we last played (a plain click without moving) so we don't tear down & replay needlessly.
  const onSeekCommit = useCallback(
    (us: number) => {
      scrubbingRef.current = false; // drag ended → let playback drive the playhead again
      setLiveTailUs(null); // any manual seek cancels live-tailing
      setPlayheadUs(us);
      setNotice(null);
      const seg = segmentAt(us);
      if (!seg) {
        // Bare spot (no recording here): issue NO read command, and stop whatever is
        // currently playing (req: clicking empty should halt the current clip).
        if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current); // cancel a queued play
        playStartUsRef.current = null;
        playEndUsRef.current = null;
        lastPlayedUsRef.current = null;
        setSource(null);
        return;
      }
      if (lastPlayedUsRef.current === us) return; // same spot already playing
      lastPlayedUsRef.current = us;
      playStartUsRef.current = null; // suspend follow until the new clip loads (keeps red line at us)
      requestPlay(us, seg.endUs);
    },
    [segmentAt, requestPlay]
  );

  // Activate a single event marker: start ~5s BEFORE the event so the lead-up is seen.
  // If event−5s isn't recorded, start at the earliest recorded point within that 5s window
  // (anywhere in the lead counts); if nothing in the window is recorded, just move the red
  // line to the event without playing. The red line tracks the actual start.
  const onEventActivate = useCallback(
    (eventTsUs: number) => {
      scrubbingRef.current = false;
      setLiveTailUs(null);
      setNotice(null);
      const target = Math.max(day.fromUs, eventTsUs - EVENT_LEAD_US);
      const seg = daySegments.find((s) => s.endUs > target && s.startUs <= eventTsUs); // overlaps [target, event]
      if (!seg) {
        if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current);
        playStartUsRef.current = null;
        playEndUsRef.current = null;
        lastPlayedUsRef.current = null;
        setPlayheadUs(eventTsUs);
        setSource(null);
        return;
      }
      const startUs = Math.max(target, seg.startUs); // clamp into the recording (within the 5s lead)
      setPlayheadUs(startUs);
      lastPlayedUsRef.current = startUs;
      playStartUsRef.current = null;
      requestPlay(startUs, seg.endUs);
    },
    [daySegments, day.fromUs, requestPlay]
  );

  // Activate a clustered marker: step the zoom DOWN one level (1day→6h→1h→15m→5m→1m),
  // centered on the cluster, so the group spreads out gradually. Only when we're already at
  // the finest zoom, or the group is so tight even 1m can't separate it, do we give up
  // zooming and just play from the earliest event −5s.
  const onClusterZoom = useCallback(
    (centerTsUs: number, earliestTsUs: number, spanUs: number) => {
      const cur = Math.max(MIN_WINDOW_US, Math.min(zoomUs, DAY_US));
      // next level DOWN = the largest option still smaller than current → sort DESC, take first.
      const finer = [...ZOOM_OPTIONS_US].sort((a, b) => b - a).find((z) => z < cur);
      const wontSeparate = spanUs <= MIN_WINDOW_US * 0.02; // ~1.2s — still clusters even at 1m (matches clusterEvents 2%)
      if (finer === undefined || wontSeparate) {
        onEventActivate(earliestTsUs);
        return;
      }
      const start = Math.max(day.fromUs, Math.min(centerTsUs - finer / 2, day.toUs - finer));
      setZoomUs(finer);
      setViewStartUs(start);
    },
    [zoomUs, day.fromUs, day.toUs, onEventActivate]
  );

  // Playhead follows playback, AND the viewport pages forward when the head reaches the
  // right edge — so playback stays on-screen at any zoom (5min…1day). Suppressed while
  // scrubbing or when no clip is loaded.
  const onPlayProgress = useCallback(
    (currentTimeSec: number) => {
      if (scrubbingRef.current) return;
      const start = playStartUsRef.current;
      if (start === null) return;
      const headUs = Math.round(start + currentTimeSec * 1_000_000);
      setPlayheadUs(headUs);
      if (headUs >= viewport.toUs) {
        const span = viewport.toUs - viewport.fromUs;
        setViewStartUs(Math.max(day.fromUs, Math.min(headUs, day.toUs - span))); // page to the next window
      }
      // Proactive prefetch at the live frontier: when nearing the end of the latest known
      // recording on the latest day (nothing known beyond this chunk), refetch day-ranges
      // AHEAD of the clip ending so a just-flushed next chunk is already known → seamless
      // continue instead of waiting after it ends.
      const endUs = playEndUsRef.current;
      if (
        endUs !== null &&
        dayIndex === latestRecordDay &&
        endUs < day.toUs &&
        headUs >= endUs - LIVE_PREFETCH_LEAD_US &&
        !segmentAt(endUs) // frontier: no known coverage past this chunk yet
      ) {
        const now = Date.now();
        if (now - lastPrefetchAtRef.current >= LIVE_PREFETCH_THROTTLE_MS) {
          lastPrefetchAtRef.current = now;
          reloadRef.current();
        }
      }
    },
    [viewport.fromUs, viewport.toUs, day.fromUs, day.toUs, dayIndex, latestRecordDay, segmentAt]
  );

  // When a clip finishes: chain straight into the next 5-min chunk if the recording
  // continues; else, on the latest-record day, tail for more like live view.
  const onPlayEnded = useCallback(() => {
    const endUs = playEndUsRef.current;
    if (endUs === null) return;
    const seg = segmentAt(endUs);
    if (seg) {
      // recording continues past this chunk → play the next one immediately (no gap)
      setLiveTailUs(null);
      lastPlayedUsRef.current = endUs;
      playStartUsRef.current = null;
      void startPlayback(endUs, seg.endUs);
      return;
    }
    // segment ended. On the latest-record day, behave like live view: poll for more.
    if (dayIndex === latestRecordDay && endUs < day.toUs) setLiveTailUs(endUs);
  }, [segmentAt, startPlayback, dayIndex, latestRecordDay, day.toUs]);

  // c5 — a /seg 404 means the ring recycled under us: re-query day-ranges and, if the
  // playhead is now before the earliest resident range, nudge it forward + rebuild.
  const onRecycle404 = useCallback(() => {
    dayRanges.reload();
    setNotice(t('recPlayback.recycled'));
    playStartUsRef.current = null;
    playEndUsRef.current = null;
    setSource(null);
  }, [dayRanges, t]);

  // Live-tail RESUME: while waiting at the live edge, re-check coverage whenever day-ranges
  // change — the moment newly-flushed recording extends past the wait point, play it.
  useEffect(() => {
    if (liveTailUs === null) return;
    const seg = daySegments.find((s) => liveTailUs >= s.startUs && liveTailUs < s.endUs);
    if (!seg) return; // nothing new yet — the poll effect will keep refetching
    setLiveTailUs(null);
    lastPlayedUsRef.current = liveTailUs;
    playStartUsRef.current = null;
    void startPlayback(liveTailUs, seg.endUs);
  }, [liveTailUs, daySegments, startPlayback]);

  // Live-tail POLL: refetch day-ranges on a cadence until coverage appears (RESUME above
  // fires) or the give-up window elapses with nothing new — i.e. recording has stopped.
  useEffect(() => {
    if (liveTailUs === null) return;
    reloadRef.current(); // poll immediately — don't wait a full interval before the first try
    const poll = setInterval(() => reloadRef.current(), LIVE_TAIL_POLL_MS);
    const giveUp = setTimeout(() => setLiveTailUs(null), LIVE_TAIL_GIVEUP_MS);
    return () => {
      clearInterval(poll);
      clearTimeout(giveUp);
    };
  }, [liveTailUs]);

  const dayOptions = useMemo(() => {
    // sdcard day-list is keyed by UTC day; expand each to the tz day(s) it overlaps, union
    // with today (in tz), newest-first.
    const set = new Set<number>();
    for (const d of dayList.days) {
      const utcFrom = d.day_index * DAY_US;
      set.add(dayIndexOf(utcFrom, offsetUs));
      // Clamp to now so today's incomplete UTC day doesn't add a phantom FUTURE tz-day option.
      set.add(dayIndexOf(Math.min(utcFrom + DAY_US - 1, Date.now() * 1000), offsetUs));
    }
    set.add(dayIndexOf(Date.now() * 1000, offsetUs));
    return [...set].sort((a, b) => b - a);
  }, [dayList.days, offsetUs]);

  return (
    <div className="rp-view">
      <header className="rp-header">
        <button
          type="button"
          className="btn-ghost rp-back"
          onClick={() => navigate(-1)}
          aria-label={t('recPlayback.back')}
        >
          ← {t('recPlayback.back')}
        </button>
      </header>
      {/* Scrubber on top: controls + timeline strip. */}
      <div className="rp-cont-controls">
        <label>
          {t('recPlayback.day')}
          <select value={dayIndex} onChange={(e) => setDayIndex(Number(e.target.value))}>
            {dayOptions.map((di) => (
              <option key={di} value={di}>
                {new Date(di * DAY_MS).toISOString().slice(0, 10)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('recPlayback.zoom')}
          <select value={zoomUs} onChange={(e) => onZoomChange(Number(e.target.value))}>
            {ZOOM_OPTIONS_US.map((z) => (
              <option key={z} value={z}>
                {z >= DAY_US ? t('recPlayback.zoom1day') : z >= 3_600_000_000 ? `${Math.round(z / 3_600_000_000)}h` : `${Math.round(z / 60_000_000)}m`}
              </option>
            ))}
          </select>
        </label>
        <span className="rp-tz-label">
          {t('recPlayback.timezone', { defaultValue: 'TZ' })}: {t('recPlayback.tzLocal', { defaultValue: 'Local' })} ({fmtTzOffset(offsetMin)})
        </span>
      </div>

      {dayRanges.state === 'UNREACHABLE' && <p className="rp-status rp-status--warn">{t('recPlayback.deviceUnreachable')}</p>}
      {notice && <p className="rp-status">{notice}</p>}

      <TimelineStrip
        range={viewport}
        clips={clips}
        events={timelineEvents}
        playheadUs={playheadUs}
        onSeek={onSeek}
        onSeekCommit={onSeekCommit}
        annotateEvents
        onEventActivate={onEventActivate}
        onClusterZoom={onClusterZoom}
        tzOffsetMin={offsetMin}
      />

      {/* Player below the scrubber, ALWAYS present (stable box → no appear/disappear
          jump when scrubbing). Empty until a clip is requested. */}
      <div className="rp-player">
        {source ? (
          <HlsPlayer source={source} loaderFactory={loaderFactory} onRecycle404={onRecycle404} onTimeUpdate={onPlayProgress} onEnded={onPlayEnded} onError={(fatal) => fatal && setNotice(t('recPlayback.playFailed'))} />
        ) : (
          <div className="rp-player-empty" aria-hidden />
        )}
      </div>
    </div>
  );
}
